import { Component, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { FilterRenderer } from '@cbre/components';
import { PropertyColumns, LeaseColumns, SaleColumns, AvailabilityColumns } from '@miq/catalog';
import { IFilter } from '@shared/models/icommons.model';
import { distinctUntilChanged, map, startWith, Subject, takeUntil } from 'rxjs';
import { PropertySetFilterLabelEventService } from './property-set-filter-label-event.service';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { IUser } from '@shared/models/iuser.model';

@Component({
  selector: 'miq-property-set-filter-label',
  template: ` <!-- Open Property set Option to the right -->
    <div class="fx--h fx--juc-space-between fx--ali-center">
      <label class="form-field__label" [attr.for]="filter.key">{{ filter.value }}</label>
      <miq-add-to-property-set-button
        text="Manage Sets"
        mode="view"
        matTooltip="View, Manage, or Create Property Sets"
        [propertyTypes]="propertyTypes"
        [markets]="markets"
        [userInfo]="user"
        (propertySetDialogEvent)="onPropertySetDialogEvent($event)"
      ></miq-add-to-property-set-button>
    </div>`,
  styleUrls: ['./property-set-filter-label.component.scss'],
  standalone: false,
})
export class PropertySetFilterLabelComponent implements FilterRenderer<IFilter> {
  @Input() filter: IFilter;
  public user: IUser;
  public markets: string[] = [];
  public propertyTypes: string[] = [];
  private _destroy = new Subject<boolean>();
  @Input() set form(form: AbstractControl) {
    this._destroy.next(true);
    this._setMarkets(form);
    this._setPropertyTypes(form);
  }

  constructor(
    private propertySetFilterLabelEventService: PropertySetFilterLabelEventService,
    private userInfo: UserInfoResolver
  ) {
    this.user = this.userInfo.resolve();
  }

  private _setMarkets = (form: AbstractControl): void => {
    const marketCtrl =
      form.get(PropertyColumns.Location_Market.toString()) ||
      form.get(LeaseColumns.Property_Market.toString()) ||
      form.get(SaleColumns.Property_Market.toString()) ||
      form.get(AvailabilityColumns.Property_Market.toString());

    if (marketCtrl == null) return;
    this._setControlListener(marketCtrl, markets => (this.markets = markets));
  };

  private _setPropertyTypes(form: AbstractControl<any, any>) {
    let propertyTypeCtrl =
      form.get(PropertyColumns.PropertyTypeDetails_Type.toString()) ||
      form.get(LeaseColumns.Property_SpaceUsageType.toString()) ||
      form.get(SaleColumns.Property_SpaceUsageType.toString()) ||
      form.get(AvailabilityColumns.SpaceDetails_SpaceUsageType.toString());

    if (propertyTypeCtrl == null) return;

    const isGroup = propertyTypeCtrl?.value?.hasOwnProperty(PropertyColumns.PropertyTypeDetails_Type.toString());
    if (isGroup) {
      propertyTypeCtrl = propertyTypeCtrl.get(PropertyColumns.PropertyTypeDetails_Type.toString());
    }
    this._setControlListener(propertyTypeCtrl, propertyTypes => (this.propertyTypes = propertyTypes));
  }

  private _setControlListener(control: AbstractControl, callback: (value: any) => void): void {
    control.valueChanges
      .pipe(
        takeUntil(this._destroy),
        startWith(control.value),
        map(value => value ?? []),
        distinctUntilChanged((a, b) => a?.join?.() === b?.join?.())
      )
      .subscribe(callback);
  }

  onPropertySetDialogEvent(evt: any) {
    this.propertySetFilterLabelEventService.triggerEvent({
      filter: this.filter,
      event: evt,
    });
  }
}
